import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Plus, X, Send, Mail, Users, Pause, Play, Trash2, Pencil, Eye,
  FileText, Clock, AlertTriangle, CheckCircle
} from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import RichTextEditor from './RichTextEditor';
import ImportHtmlPanel from './ImportHtmlPanel';
import emailApi, {
  Campaign, CampaignRecipient, EmailTemplate, Audience, STATUS_LABEL,
} from '../../services/emailApi';
import '../../styles/mentee-dashboard.scss';
import '../plan/plan.scss';
import '../../styles/admin-emails.scss';

const ROLE_OPTIONS = [
  { key: 'mentor', label: 'Mentores' },
  { key: 'mentee', label: 'Mentees' },
  { key: 'admin', label: 'Administradores' },
];

const fmt = (d?: string | null) => {
  if (!d) return '—';
  const date = new Date(String(d).replace(' ', 'T'));
  return isNaN(date.getTime()) ? String(d) : date.toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' });
};

const AdminEmails: React.FC = () => {
  const [tab, setTab] = useState<'campaigns' | 'templates'>('campaigns');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [testRedirect, setTestRedirect] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Campaign | 'new' | null>(null);
  const [detail, setDetail] = useState<Campaign | null>(null);
  const [editingTpl, setEditingTpl] = useState<EmailTemplate | 'new' | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [cs, ts] = await Promise.all([emailApi.list(), emailApi.templates()]);
      setCampaigns(cs);
      setTemplates(ts.data || []);
      setVariables(ts.variables || {});
      setTestRedirect(ts.test_redirect || null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudieron cargar las campañas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Mientras haya una campaña en curso, el panel se refresca solo.
  const anySending = campaigns.some(c => c.status === 'sending' || c.status === 'scheduled');
  useEffect(() => {
    if (!anySending) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [anySending, load]);

  const act = async (fn: () => Promise<any>) => {
    setError('');
    try { await fn(); await load(); }
    catch (e: any) { setError(e?.response?.data?.message || 'No se pudo completar la acción'); }
  };

  return (
    <MainLayout>
      <div className="mentee-dashboard sessions-view">
        <div className="sv-eyebrow">Administración</div>
        <h1 className="sv-title">Envío de <em>Correos</em></h1>
        <p className="sv-lead">
          Escribe un correo, elige a quién va y envíalo. La entrega ocurre en segundo plano,
          así que puedes cerrar esta pantalla y volver luego a ver el avance.
        </p>

        {testRedirect && (
          <div className="em-testmode">
            <AlertTriangle size={15} />
            <span>
              <strong>Modo prueba activo.</strong> Todos los correos se desvían a <b>{testRedirect}</b> y
              nadie más los recibe. Para enviar de verdad, quita <code>email.testRedirect</code> del .env
              del backend y reinicia el worker.
            </span>
          </div>
        )}

        <div className="sessions-page">
          <div className="em-tabs">
            <button className={`dupla-chip ${tab === 'campaigns' ? 'on' : ''}`} onClick={() => setTab('campaigns')}>
              Campañas ({campaigns.length})
            </button>
            <button className={`dupla-chip ${tab === 'templates' ? 'on' : ''}`} onClick={() => setTab('templates')}>
              Plantillas ({templates.length})
            </button>
            <div className="em-tabs-right">
              {tab === 'campaigns' ? (
                <button className="btn-ink" onClick={() => setEditing('new')}><Plus size={15} /> Nueva campaña</button>
              ) : (
                <button className="btn-ink" onClick={() => setEditingTpl('new')}><Plus size={15} /> Nueva plantilla</button>
              )}
            </div>
          </div>

          {error && <div className="sf-empty">{error}</div>}

          {loading ? (
            <p className="dash-empty">Cargando…</p>
          ) : tab === 'campaigns' ? (
            campaigns.length === 0 ? (
              <p className="dash-empty">Todavía no has creado campañas. Empieza con “Nueva campaña”.</p>
            ) : (
              <div className="em-list">
                {campaigns.map(c => (
                  <CampaignRow
                    key={c.id}
                    campaign={c}
                    onOpen={() => setDetail(c)}
                    onEdit={() => setEditing(c)}
                    onPause={() => act(() => emailApi.pause(c.id))}
                    onResume={() => act(() => emailApi.resume(c.id))}
                    onCancel={() => {
                      if (window.confirm(`¿Cancelar “${c.name}”? Los correos pendientes no se enviarán.`)) {
                        act(() => emailApi.cancel(c.id));
                      }
                    }}
                    onDelete={() => {
                      if (window.confirm(`¿Eliminar “${c.name}”? Se borra también su historial de destinatarios.`)) {
                        act(() => emailApi.remove(c.id));
                      }
                    }}
                  />
                ))}
              </div>
            )
          ) : (
            <TemplateList
              templates={templates}
              onEdit={setEditingTpl}
              onDelete={(t) => {
                if (window.confirm(`¿Eliminar la plantilla “${t.name}”?`)) act(() => emailApi.deleteTemplate(t.id));
              }}
            />
          )}
        </div>

        {editing && (
          <CampaignEditor
            campaign={editing === 'new' ? null : editing}
            templates={templates}
            variables={variables}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}

        {editingTpl && (
          <TemplateEditor
            template={editingTpl === 'new' ? null : editingTpl}
            variables={variables}
            onClose={() => setEditingTpl(null)}
            onSaved={() => { setEditingTpl(null); load(); }}
          />
        )}

        {detail && <CampaignDetail campaign={detail} onClose={() => setDetail(null)} />}
      </div>
    </MainLayout>
  );
};

// ------------------------------------------------------------------ listado

const CampaignRow: React.FC<{
  campaign: Campaign;
  onOpen: () => void; onEdit: () => void;
  onPause: () => void; onResume: () => void; onCancel: () => void; onDelete: () => void;
}> = ({ campaign: c, onOpen, onEdit, onPause, onResume, onCancel, onDelete }) => {
  const done = c.sent_count + c.failed_count;
  const pct = c.total_recipients ? Math.round((done / c.total_recipients) * 100) : 0;
  const editable = c.status === 'draft' || c.status === 'scheduled' || c.status === 'cancelled';

  return (
    <div className={`em-card em-card--${c.status}`}>
      <div className="em-card-main">
        <div className="em-card-head">
          <span className={`em-status em-status--${c.status}`}>{STATUS_LABEL[c.status]}</span>
          <span className="em-card-name">{c.name}</span>
        </div>
        <div className="em-card-subject">{c.subject}</div>

        {c.total_recipients > 0 && (
          <>
            <div className="em-bar"><div className="em-bar-fill" style={{ width: `${pct}%` }} /></div>
            <div className="em-card-meta">
              <span><CheckCircle size={12} /> {c.sent_count} enviados</span>
              {c.failed_count > 0 && <span className="em-fail"><AlertTriangle size={12} /> {c.failed_count} fallidos</span>}
              {c.pending_count > 0 && <span><Clock size={12} /> {c.pending_count} en cola</span>}
              <span>· {c.total_recipients} destinatarios</span>
            </div>
          </>
        )}

        <div className="em-card-meta">
          {c.status === 'scheduled' && c.scheduled_at && <span><Clock size={12} /> Programada: {fmt(c.scheduled_at)}</span>}
          {c.finished_at && <span>Finalizada: {fmt(c.finished_at)}</span>}
          {!c.finished_at && c.status === 'draft' && <span>Creada: {fmt(c.created_at)}</span>}
          {c.created_by_name && <span>· {c.created_by_name}</span>}
        </div>
      </div>

      <div className="em-card-actions">
        <button className="btn-ink" onClick={onOpen}><Eye size={14} /> Ver</button>
        {editable && <button className="btn-ghost btn-icon" onClick={onEdit} title="Editar"><Pencil size={14} /></button>}
        {c.status === 'sending' && <button className="btn-ghost btn-icon" onClick={onPause} title="Pausar"><Pause size={14} /></button>}
        {(c.status === 'paused' || c.status === 'scheduled') && <button className="btn-ghost btn-icon" onClick={onResume} title="Reanudar ahora"><Play size={14} /></button>}
        {['sending', 'paused', 'scheduled'].includes(c.status) && <button className="btn-ghost btn-icon" onClick={onCancel} title="Cancelar"><X size={14} /></button>}
        {c.status !== 'sending' && <button className="btn-ghost btn-icon" onClick={onDelete} title="Eliminar"><Trash2 size={14} /></button>}
      </div>
    </div>
  );
};

const TemplateList: React.FC<{
  templates: EmailTemplate[];
  onEdit: (t: EmailTemplate) => void;
  onDelete: (t: EmailTemplate) => void;
}> = ({ templates, onEdit, onDelete }) => {
  if (templates.length === 0) {
    return <p className="dash-empty">Aún no hay plantillas. Guarda una para reutilizar el contenido en próximos envíos.</p>;
  }
  return (
    <div className="em-list">
      {templates.map(t => (
        <div className="em-card" key={t.id}>
          <div className="em-card-main">
            <div className="em-card-head">
              <span className="em-status em-status--draft"><FileText size={11} /> Plantilla</span>
              <span className="em-card-name">{t.name}</span>
            </div>
            <div className="em-card-subject">{t.subject}</div>
            {t.description && <div className="em-card-meta"><span>{t.description}</span></div>}
          </div>
          <div className="em-card-actions">
            <button className="btn-ink" onClick={() => onEdit(t)}><Pencil size={14} /> Editar</button>
            <button className="btn-ghost btn-icon" onClick={() => onDelete(t)} title="Eliminar"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ------------------------------------------------------- editor de campaña

const CampaignEditor: React.FC<{
  campaign: Campaign | null;
  templates: EmailTemplate[];
  variables: Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
}> = ({ campaign, templates, variables, onClose, onSaved }) => {
  const [id, setId] = useState<number | null>(campaign?.id ?? null);
  const [name, setName] = useState(campaign?.name || '');
  const [subject, setSubject] = useState(campaign?.subject || '');
  const [html, setHtml] = useState(campaign?.html || '<p>Hola {{nombre}},</p>\n<p>Escribe aquí tu mensaje.</p>');
  const [ctaLabel, setCtaLabel] = useState(campaign?.cta_label || '');
  const [ctaUrl, setCtaUrl] = useState(campaign?.cta_url || '');
  const [useLayout, setUseLayout] = useState(campaign ? campaign.use_layout !== 0 : true);
  const [mode, setMode] = useState<'visual' | 'import'>(campaign && campaign.use_layout === 0 ? 'import' : 'visual');
  const [roles, setRoles] = useState<string[]>(campaign?.audience?.roles || []);
  const [includeTest, setIncludeTest] = useState(!!campaign?.audience?.include_test);
  const [scheduledAt, setScheduledAt] = useState('');
  const [testEmail, setTestEmail] = useState('');

  const [preview, setPreview] = useState('');
  const [audienceCount, setAudienceCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const previewTimer = useRef<any>(null);

  const audience: Audience = { roles, user_ids: [], include_test: includeTest };

  // Previsualización con datos de ejemplo, con un respiro para no pedirla en cada tecla.
  useEffect(() => {
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        const r = await emailApi.preview({ subject, html, cta_label: ctaLabel, cta_url: ctaUrl, use_layout: useLayout });
        setPreview(r.html);
      } catch { /* la previsualización no bloquea la edición */ }
    }, 400);
    return () => clearTimeout(previewTimer.current);
  }, [subject, html, ctaLabel, ctaUrl, useLayout]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (roles.length === 0) { setAudienceCount(0); return; }
      try {
        const r = await emailApi.audience(audience);
        if (alive) setAudienceCount(r.total);
      } catch { if (alive) setAudienceCount(null); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles.join(','), includeTest]);

  const toggleRole = (key: string) =>
    setRoles(prev => prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]);

  const applyTemplate = (templateId: string) => {
    const t = templates.find(x => String(x.id) === templateId);
    if (!t) return;
    setSubject(t.subject);
    setHtml(t.html);
    setCtaLabel(t.cta_label || '');
    setCtaUrl(t.cta_url || '');
    setUseLayout(t.use_layout !== 0);
    setMode(t.use_layout === 0 ? 'import' : 'visual');
  };

  const validate = () => {
    if (name.trim().length < 3) return 'Ponle un nombre a la campaña';
    if (subject.trim().length < 3) return 'Escribe el asunto del correo';
    if (html.trim().length < 10) return 'El correo no puede ir vacío';
    return '';
  };

  const save = async (): Promise<number | null> => {
    const problem = validate();
    if (problem) { setError(problem); return null; }

    const payload = {
      name, subject, html,
      cta_label: ctaLabel || null, cta_url: ctaUrl || null,
      use_layout: useLayout ? 1 : 0,
      audience,
    } as any;

    if (id) { await emailApi.update(id, payload); return id; }
    const r = await emailApi.create(payload);
    setId(r.id);
    return r.id;
  };

  const run = async (fn: () => Promise<void>) => {
    setError(''); setNotice(''); setBusy(true);
    try { await fn(); }
    catch (e: any) { setError(e?.response?.data?.message || 'No se pudo completar la acción'); }
    finally { setBusy(false); }
  };

  const onSaveDraft = () => run(async () => {
    const saved = await save();
    if (saved) { setNotice('Borrador guardado'); onSaved(); }
  });

  const onSendTest = () => run(async () => {
    if (!testEmail.trim()) { setError('Escribe el correo donde quieres recibir la prueba'); return; }
    const saved = await save();
    if (!saved) return;
    const r = await emailApi.sendTest(saved, testEmail.trim());
    setNotice(r.message || 'Prueba enviada');
  });

  const onSend = () => run(async () => {
    if (roles.length === 0) { setError('Elige al menos un grupo de destinatarios'); return; }
    const when = scheduledAt ? `programar para ${scheduledAt.replace('T', ' ')}` : `enviar ahora a ${audienceCount ?? '…'} personas`;
    if (!window.confirm(`¿Confirmas ${when}?`)) return;

    const saved = await save();
    if (!saved) return;
    const r = await emailApi.send(saved, scheduledAt || null);
    setNotice(r.message || 'Campaña encolada');
    onSaved();
  });

  return (
    <div className="feedback-modal-overlay">
      <div className="feedback-modal em-editor" onClick={e => e.stopPropagation()}>
        <button className="sf-close" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        <h3>{id ? 'Editar campaña' : 'Nueva campaña'}</h3>

        {error && <div className="sf-empty">{error}</div>}
        {notice && <div className="em-notice"><CheckCircle size={14} /> {notice}</div>}

        <div className="em-editor-grid">
          <div className="em-editor-form">
            <div className="sf-label">Nombre interno <span className="sf-hint">solo lo ves tú</span></div>
            <input className="sf-input" value={name} onChange={e => setName(e.target.value)}
              placeholder="Ej.: Recordatorio de sesiones · marzo" />

            {templates.length > 0 && (
              <>
                <div className="sf-label">Partir de una plantilla</div>
                <select className="sf-input" defaultValue="" onChange={e => applyTemplate(e.target.value)}>
                  <option value="">Escribir desde cero…</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </>
            )}

            <div className="sf-label">Asunto</div>
            <input className="sf-input" value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="Ej.: {{nombre}}, agenda tu sesión de este mes" />

            <div className="sf-label">Contenido</div>
            <div className="em-modes">
              <button type="button" className={`dupla-chip ${mode === 'visual' ? 'on' : ''}`}
                onClick={() => { setMode('visual'); setUseLayout(true); }}>Escribir aquí</button>
              <button type="button" className={`dupla-chip ${mode === 'import' ? 'on' : ''}`}
                onClick={() => { setMode('import'); setUseLayout(false); }}>Pegar HTML de otro editor</button>
            </div>

            {mode === 'visual' ? (
              <RichTextEditor value={html} onChange={setHtml} variables={variables} minHeight={300} />
            ) : (
              <ImportHtmlPanel
                html={html}
                variables={variables}
                useLayout={useLayout}
                onUseLayoutChange={setUseLayout}
                onChange={setHtml}
              />
            )}

            <div className="sf-row">
              <div>
                <div className="sf-label">Botón <span className="sf-hint">opcional</span></div>
                <input className="sf-input" value={ctaLabel} onChange={e => setCtaLabel(e.target.value)}
                  placeholder="Ir a la plataforma" />
              </div>
              <div>
                <div className="sf-label">Enlace del botón</div>
                <input className="sf-input" value={ctaUrl} onChange={e => setCtaUrl(e.target.value)}
                  placeholder="https://mitsui.kuali.ai" />
              </div>
            </div>

            <div className="sf-label">Destinatarios</div>
            <div className="res-chips">
              {ROLE_OPTIONS.map(r => (
                <button key={r.key} type="button"
                  className={`dupla-chip ${roles.includes(r.key) ? 'on' : ''}`}
                  onClick={() => toggleRole(r.key)}>{r.label}</button>
              ))}
            </div>
            <label className="em-check">
              <input type="checkbox" checked={includeTest} onChange={e => setIncludeTest(e.target.checked)} />
              <span>Incluir cuentas de prueba</span>
            </label>
            <div className="em-audience">
              <Users size={14} /> {audienceCount === null ? 'Calculando…' : `${audienceCount} destinatario(s)`}
            </div>

            <div className="sf-row">
              <div>
                <div className="sf-label">Enviar prueba a</div>
                <input className="sf-input" type="email" value={testEmail}
                  onChange={e => setTestEmail(e.target.value)} placeholder="tu@correo.com" />
              </div>
              <div>
                <div className="sf-label">Programar <span className="sf-hint">opcional</span></div>
                <input className="sf-input" type="datetime-local" value={scheduledAt}
                  onChange={e => setScheduledAt(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="em-editor-preview">
            <div className="sf-label">Previsualización <span className="sf-hint">con datos de ejemplo</span></div>
            <iframe className="em-preview-frame" title="Previsualización" srcDoc={preview} />
          </div>
        </div>

        <div className="feedback-modal__actions em-actions">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>Cerrar</button>
          <button className="btn-ghost" onClick={onSaveDraft} disabled={busy}>Guardar borrador</button>
          <button className="btn-ghost" onClick={onSendTest} disabled={busy}><Mail size={14} /> Enviar prueba</button>
          <button className="btn-ink" onClick={onSend} disabled={busy}>
            <Send size={14} /> {scheduledAt ? 'Programar envío' : 'Enviar ahora'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------ editor de plantilla

const TemplateEditor: React.FC<{
  template: EmailTemplate | null;
  variables: Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
}> = ({ template, variables, onClose, onSaved }) => {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [html, setHtml] = useState(template?.html || '<p>Hola {{nombre}},</p>\n<p>…</p>');
  const [ctaLabel, setCtaLabel] = useState(template?.cta_label || '');
  const [ctaUrl, setCtaUrl] = useState(template?.cta_url || '');
  const [useLayout, setUseLayout] = useState(template ? template.use_layout !== 0 : true);
  const [mode, setMode] = useState<'visual' | 'import'>(template && template.use_layout === 0 ? 'import' : 'visual');
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const timer = useRef<any>(null);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await emailApi.preview({ subject, html, cta_label: ctaLabel, cta_url: ctaUrl, use_layout: useLayout });
        setPreview(r.html);
      } catch { /* ignorar */ }
    }, 400);
    return () => clearTimeout(timer.current);
  }, [subject, html, ctaLabel, ctaUrl, useLayout]);

  const save = async () => {
    setError('');
    if (name.trim().length < 3) { setError('Ponle un nombre a la plantilla'); return; }
    if (subject.trim().length < 3) { setError('Escribe el asunto'); return; }
    try {
      setBusy(true);
      const payload = { name, subject, html, description, cta_label: ctaLabel || null, cta_url: ctaUrl || null, use_layout: useLayout ? 1 : 0 };
      if (template) await emailApi.updateTemplate(template.id, payload);
      else await emailApi.createTemplate(payload);
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo guardar la plantilla');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="feedback-modal-overlay">
      <div className="feedback-modal em-editor" onClick={e => e.stopPropagation()}>
        <button className="sf-close" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        <h3>{template ? 'Editar plantilla' : 'Nueva plantilla'}</h3>
        {error && <div className="sf-empty">{error}</div>}

        <div className="em-editor-grid">
          <div className="em-editor-form">
            <div className="sf-label">Nombre</div>
            <input className="sf-input" value={name} onChange={e => setName(e.target.value)}
              placeholder="Ej.: Recordatorio mensual de sesión" />

            <div className="sf-label">Para qué sirve <span className="sf-hint">opcional</span></div>
            <input className="sf-input" value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Cuándo conviene usarla" />

            <div className="sf-label">Asunto</div>
            <input className="sf-input" value={subject} onChange={e => setSubject(e.target.value)} />

            <div className="sf-label">Contenido</div>
            <div className="em-modes">
              <button type="button" className={`dupla-chip ${mode === 'visual' ? 'on' : ''}`}
                onClick={() => { setMode('visual'); setUseLayout(true); }}>Escribir aquí</button>
              <button type="button" className={`dupla-chip ${mode === 'import' ? 'on' : ''}`}
                onClick={() => { setMode('import'); setUseLayout(false); }}>Pegar HTML de otro editor</button>
            </div>

            {mode === 'visual' ? (
              <RichTextEditor value={html} onChange={setHtml} variables={variables} minHeight={340} />
            ) : (
              <ImportHtmlPanel
                html={html}
                variables={variables}
                useLayout={useLayout}
                onUseLayoutChange={setUseLayout}
                onChange={setHtml}
              />
            )}

            <div className="sf-row">
              <div>
                <div className="sf-label">Botón <span className="sf-hint">opcional</span></div>
                <input className="sf-input" value={ctaLabel} onChange={e => setCtaLabel(e.target.value)} />
              </div>
              <div>
                <div className="sf-label">Enlace del botón</div>
                <input className="sf-input" value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="em-editor-preview">
            <div className="sf-label">Previsualización</div>
            <iframe className="em-preview-frame" title="Previsualización" srcDoc={preview} />
          </div>
        </div>

        <div className="feedback-modal__actions em-actions">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className="btn-ink" onClick={save} disabled={busy}>{busy ? 'Guardando…' : 'Guardar plantilla'}</button>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------- detalle

const CampaignDetail: React.FC<{ campaign: Campaign; onClose: () => void }> = ({ campaign, onClose }) => {
  const [data, setData] = useState<{ campaign: Campaign; recipients: CampaignRecipient[] } | null>(null);
  const [filter, setFilter] = useState<'all' | 'sent' | 'failed' | 'pending'>('all');

  const load = useCallback(async () => {
    try { setData(await emailApi.get(campaign.id)); }
    catch { /* ignorar */ }
  }, [campaign.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data?.campaign.status !== 'sending') return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [data?.campaign.status, load]);

  const c = data?.campaign || campaign;
  const recipients = (data?.recipients || []).filter(r =>
    filter === 'all' ? true : filter === 'pending' ? (r.status === 'pending' || r.status === 'sending') : r.status === filter
  );

  return (
    <div className="feedback-modal-overlay" onClick={onClose}>
      <div className="feedback-modal em-detail" onClick={e => e.stopPropagation()}>
        <button className="sf-close" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        <h3>{c.name}</h3>
        <p className="feedback-modal__sub">{c.subject}</p>

        <div className="em-stats">
          <div className="em-stat"><span className="em-stat-val">{c.total_recipients}</span><span className="em-stat-lbl">Destinatarios</span></div>
          <div className="em-stat"><span className="em-stat-val">{c.sent_count}</span><span className="em-stat-lbl">Enviados</span></div>
          <div className="em-stat"><span className="em-stat-val">{c.pending_count}</span><span className="em-stat-lbl">En cola</span></div>
          <div className="em-stat"><span className="em-stat-val em-stat-val--red">{c.failed_count}</span><span className="em-stat-lbl">Fallidos</span></div>
        </div>

        <div className="res-chips em-detail-filters">
          {(['all', 'sent', 'pending', 'failed'] as const).map(f => (
            <button key={f} className={`dupla-chip ${filter === f ? 'on' : ''}`} onClick={() => setFilter(f)}>
              {f === 'all' ? 'Todos' : f === 'sent' ? 'Enviados' : f === 'pending' ? 'En cola' : 'Fallidos'}
            </button>
          ))}
        </div>

        <div className="em-recipients">
          {recipients.length === 0 ? (
            <p className="dash-empty">Sin destinatarios en este filtro.</p>
          ) : recipients.map(r => (
            <div className="em-recipient" key={r.id}>
              <span className={`em-dot em-dot--${r.status}`} />
              <span className="em-recipient-name">{r.name || r.email}</span>
              <span className="em-recipient-mail">{r.email}</span>
              <span className="em-recipient-when">{r.sent_at ? fmt(r.sent_at) : r.last_error || '—'}</span>
            </div>
          ))}
        </div>

        <div className="feedback-modal__actions em-actions">
          <button className="btn-ink" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
};

export default AdminEmails;
